import { LinkExternalProviderDialog } from '@/components/molecules/LinkExternalProviderDialog';
import { UnlinkExternalProviderConfirmDialog } from '@/components/molecules/UnlinkExternalProviderConfirmDialog';
import { useTRPC } from '@/utils/trpc';
import type { PatientAdminApiRouterOutputs } from '@liza/patient-admin-api';
import { QueryStateHandler } from '@liza/ui/components/QueryStateHandler';
import { CareTeamSections, type ExternalCareTeamMemberDisplay } from '@liza/ui/components/records/CareTeamSections';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

type Patient = PatientAdminApiRouterOutputs['patient']['get']['patient'];

type Props = {
  patient: Patient;
};

export const CareTeam = ({ patient }: Props): React.ReactElement => {
  const { t } = useTranslation('common');
  const { t: tRecords } = useTranslation('records');
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState<ExternalCareTeamMemberDisplay | null>(null);
  const [unlinkOpen, setUnlinkOpen] = useState(false);

  const practitionersQuery = useQuery(
    trpc.practitioner.list.queryOptions({
      organizationId: patient.organizationId,
      pageIndex: 0,
      pageSize: 100,
    }),
  );

  const externalQuery = useQuery(trpc.externalCareTeamMember.listForPatient.queryOptions({ patientId: patient.id }));

  const unlinkMutation = useMutation(
    trpc.externalCareTeamMember.unlinkFromPatient.mutationOptions({
      onSuccess: () => {
        toast.success(t('externalProviderUnlinked'));
        void queryClient.invalidateQueries({ queryKey: trpc.externalCareTeamMember.listForPatient.queryKey() });
        setUnlinkOpen(false);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  const external: ExternalCareTeamMemberDisplay[] = (externalQuery.data?.members ?? []).map((member) => ({
    roleId: member.roleId,
    name: member.name,
    practiceName: member.organizationName,
    specialty: member.specialty?.display,
    providerType: member.typeCode?.display,
  }));

  return (
    <div className="flex flex-col gap-2 p-2">
      {/* practitioner.list returns an object, so QueryStateHandler's array-only empty check never
          fires; CareTeamSections renders the empty state. emptyMessage is the required-prop fallback. */}
      <QueryStateHandler query={practitionersQuery} emptyMessage={tRecords('noCareTeamMembers')}>
        {(data) => (
          <CareTeamSections
            internal={data.practitioners}
            external={external}
            onAddExternalProvider={() => setLinkDialogOpen(true)}
            onUnlinkExternal={(member) => {
              setUnlinkTarget(member);
              setUnlinkOpen(true);
            }}
          />
        )}
      </QueryStateHandler>
      <LinkExternalProviderDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        organizationId={patient.organizationId}
        patientId={patient.id}
        linkedRoleIds={external.map((member) => member.roleId)}
      />
      {/* unlinkTarget survives close (only unlinkOpen flips) so the dialog can play its exit transition instead of unmounting mid-close. */}
      {unlinkTarget ? (
        <UnlinkExternalProviderConfirmDialog
          open={unlinkOpen}
          onOpenChange={setUnlinkOpen}
          memberName={unlinkTarget.name}
          isPending={unlinkMutation.isPending}
          onConfirm={() => unlinkMutation.mutate({ patientId: patient.id, roleId: unlinkTarget.roleId })}
        />
      ) : null}
    </div>
  );
};
